// StateTracker — the ring of per-update StateFrames + the per-block hash fold reported in
// CsUpdateResult (the syncHash the .wirep replay oracle records). game/statetracker.{h,cpp}.

import { u32 } from '../math/int32.ts';
import { StateFrame } from './stateframe.ts';

// game/statetracker.cpp:9
const kcFramesMax = 200;

export class StateTracker {
  private frames_: StateFrame[] = [];
  private framesFree_: StateFrame[] = [];
  private gameid_: number;
  private nextblock_ = true; // game/statetracker.cpp:19 — the first frame is a block frame

  // game/statetracker.cpp:13-20
  constructor(gameid: number) {
    this.gameid_ = u32(gameid);
  }

  /** TS-only seam: the C++ constructs the tracker WITH the game id
   *  (SimUIForm::Init, game/SimUI.cpp:180 `new StateTracker(m_gameid)`) but the
   *  TS SimContext pre-builds it before the session knows its id — the
   *  GameSession ctor stamps it here. gameid_ only feeds ToJson's "gameid"
   *  field (the ReportSyncError upload payload). */
  SetGameId(gameid: number): void {
    this.gameid_ = u32(gameid);
  }

  // game/statetracker.h:20
  SetNextBlock(): void {
    this.nextblock_ = true;
  }

  // game/statetracker.cpp:34-64
  AddFrame(cUpdates: number): StateFrame {
    if (this.frames_.length >= kcFramesMax) this.MoveToFreeList(1);

    const frame = this.framesFree_.pop() ?? new StateFrame(this);
    frame.Init(cUpdates, this.nextblock_);
    this.nextblock_ = false;

    this.frames_.push(frame);
    return frame;
  }

  // game/statetracker.cpp:66-82 — expire frames older than cUpdates (but not cUpdates
  // itself: the client may still need it for the block-hash calculation).
  ExpireFrames(cUpdates: number): void {
    let i = 0;
    for (; i < this.frames_.length; i++) {
      if (this.frames_[i].updates() >= cUpdates) break;
    }
    this.MoveToFreeList(i);
  }

  // game/statetracker.cpp:84-97
  private MoveToFreeList(count: number): void {
    if (count === 0) return;
    for (let i = 0; i < count; i++) this.framesFree_.push(this.frames_[i]);
    this.frames_.splice(0, count);
  }

  // game/statetracker.cpp:128-143 — fold newest→oldest until (and including) the last
  // block frame. The variable shift makes the fold order-sensitive.
  GetHash(): number {
    let h = 0;
    for (let i = this.frames_.length - 1; i >= 0; i--) {
      const frame = this.frames_[i];
      const t = frame.GetHash();
      h = u32((h << (h & 3)) ^ t); // dword shift wraps mod 2^32; JS int32 bit ops match
      if (frame.block()) break;
    }
    return h;
  }

  // game/statetracker.cpp:99-126 — {"gameid":"%x","frame0":{...},"frame1":{...}}
  ToJson(): string {
    let out = '{';
    out += `"gameid":"${this.gameid_.toString(16)}",`;
    for (let i = 0; i < this.frames_.length; i++) {
      if (i !== 0) out += ',';
      out += `"frame${i}":`;
      out += this.frames_[i].ToJson();
    }
    out += '}';
    return out;
  }
}
